import { useEffect, type JSX } from "react";
import { useTranslation } from "react-i18next";

import { EmptyState } from "@/shared/components/EmptyState";
import { Pagination } from "@/shared/components/Pagination";
import { SectionCard } from "@/shared/components/SectionCard";
import { toast } from "@/shared/lib/toast";
import { Skeleton } from "@/shared/ui/skeleton";

import { fetchAllUnmappedCodes, useUnmappedSkus } from "../api/use-unmapped-skus";

interface UnmappedSkuPanelProps {
  search: string;
  offset: number;
  limit: number;
  onPageChange: (next: { offset: number; limit: number }) => void;
  /** Surfaces the unmapped total to the parent (e.g. for the page subtitle). */
  onTotalChange?: (total: number) => void;
}

const TH =
  "h-10 px-4 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)]";
const TD = "h-[52px] px-4 text-sm text-[var(--text-primary)]";

export function UnmappedSkuPanel({
  search,
  offset,
  limit,
  onPageChange,
  onTotalChange,
}: UnmappedSkuPanelProps): JSX.Element {
  const { t } = useTranslation("inventory");
  const { data, isLoading } = useUnmappedSkus({ search, offset, limit });
  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  useEffect(() => {
    onTotalChange?.(total);
  }, [total, onTotalChange]);

  async function copyAll(): Promise<void> {
    try {
      const codes = await fetchAllUnmappedCodes(search);
      if (!navigator.clipboard) throw new Error("clipboard unavailable");
      await navigator.clipboard.writeText(codes.join("\n"));
      toast.success(t("unmapped.copied", { n: codes.length }));
    } catch {
      toast.error(t("unmapped.copy_failed"));
    }
  }

  return (
    <div data-testid="unmapped-panel" className="space-y-3">
      <SectionCard
        title={t("unmapped.title")}
        caption={t("unmapped.caption", { n: total })}
        actions={
          <button
            type="button"
            data-testid="unmapped-copy"
            disabled={total === 0}
            onClick={() => void copyAll()}
            className="inline-flex h-[var(--control-h)] items-center rounded-[var(--control-r)] border border-[var(--premium-border-strong)] bg-white px-3.5 text-[13.5px] font-semibold text-[var(--text-primary)] shadow-[var(--sh-xs)] transition hover:bg-[var(--canvas)] disabled:opacity-50"
          >
            {t("unmapped.copy")}
          </button>
        }
      >
        {isLoading ? (
          <div className="space-y-2 p-4">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton key={i} className="h-9" />
            ))}
          </div>
        ) : items.length === 0 ? (
          <EmptyState title={t("unmapped.empty")} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr>
                  <th className={TH}>{t("column.sku")}</th>
                  <th className={TH}>{t("column.product")}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((s) => (
                  <tr
                    key={s.sino_sku}
                    data-testid="unmapped-row"
                    className="border-t border-[var(--row-divider)] hover:bg-[var(--row-hover)]"
                  >
                    <td className={`${TD} sku`}>{s.sino_sku}</td>
                    <td className={TD}>{s.product_name}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
      <Pagination total={total} offset={offset} limit={limit} onChange={onPageChange} />
    </div>
  );
}
